import {
  AzureOfferPlan,
  AzurePricingType,
  RecurrentPrice,
} from '../../../modules/privateOffer/types'
import clsx from 'clsx'
import { useStyles } from './AzurePricingSection.styles'
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { TextInput } from '@labrav/react-components'
import { DisplayValueData } from './DisplayValueData'
import { getDiscountedPriceValue, isNonEmptyNumber } from '@labrav/common'
import { cloneDeep, get, isEmpty } from 'lodash'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { useCallback } from 'react'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import type { ProductPlan } from '../../../modules/productsListing/types'
import { AddUpdateButton } from '../../../common/components/AddUpdateButton'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { useCommonStyles } from '../../../common/styles/common.styles'
import {
  convertProductPlanToOfferPlan,
  emptyBillingTerms,
  getBillingTermsList,
  getPaymentOptionDisplayValue,
} from './utils'

export interface BillingTermsProps {
  planRow: AzureOfferPlan
  index: number
  updatePlanId?: (
    index: number,
    plan: AzureOfferPlan,
    keyForErrorCheck?: string,
    subIndex?: number,
    meter?: string,
    deleteBillingTerms?: boolean
  ) => Promise<void>
  planRowError?: Record<string, unknown>
  azurePricingType: AzurePricingType
  isReadOnly?: boolean
  isViewPage?: boolean
  productPlan?: ProductPlan
}

export const BillingTerms = ({
  index,
  azurePricingType,
  planRow,
  planRowError,
  updatePlanId,
  isReadOnly = false,
  isViewPage = false,
  productPlan,
}: BillingTermsProps) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const isDiscountPricingType =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const billingTermsColumn = [
    'Billing term',
    'Payment option',
    `${isDiscountPricingType ? 'Discounted price' : 'Price per payment'} (USD)`,
  ]

  const originalPricingData = { ...planRow?.originalPricing?.recurrentPrice }

  const recurrentPrices =
    !isDiscountPricingType || isViewPage
      ? planRow?.pricing?.recurrentPrice?.prices
        ? [...planRow.pricing.recurrentPrice.prices]
        : []
      : originalPricingData.prices
        ? [...originalPricingData.prices]
        : []

  const billingTermsData = [...recurrentPrices]

  const shouldShowInput = !(isDiscountPricingType || isReadOnly)

  const updatePrice =
    (priceIndex: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const updatedPrices = cloneDeep(recurrentPrices)
      updatedPrices[priceIndex].pricePerPaymentInUsd =
        event.target.value !== ''
          ? parseFloat(Number(event.target.value).toFixed(3))
          : undefined

      const updatedRecurrentPrice = {
        ...planRow.pricing.recurrentPrice,
        prices: [...updatedPrices],
      }

      const updatePlanRow = {
        ...planRow,
        pricing: {
          ...planRow.pricing,
          recurrentPrice: {
            ...updatedRecurrentPrice,
          },
        },
      }
      updatePlanId &&
        updatePlanId(index, { ...updatePlanRow }, 'paymentOption', priceIndex)
    }

  const deleteBillingTerms = (billingIndex: number) => {
    const updatedPrices = cloneDeep(recurrentPrices)

    const updatedRecurrentPrice = {
      ...planRow.pricing.recurrentPrice,
      prices: [...updatedPrices].filter(
        (item, index) => index !== billingIndex
      ),
    }

    const updatePlanRow = {
      ...planRow,
      pricing: {
        ...planRow.pricing,
        recurrentPrice: {
          ...updatedRecurrentPrice,
        },
      },
      originalPricing: {
        ...planRow.originalPricing,
        recurrentPrice: {
          ...(planRow.originalPricing?.recurrentPrice as RecurrentPrice),
          prices: [
            ...(planRow.originalPricing?.recurrentPrice?.prices || []),
          ].filter((item, index) => index !== billingIndex),
        },
      },
    }
    updatePlanId &&
      updatePlanId(
        index,
        { ...updatePlanRow },
        'paymentOption',
        billingIndex,
        '',
        true
      )
  }

  const tableDataCellClass = clsx(
    classes.rowText,
    classes.tableCell,
    classes.alignMiddel,
    isReadOnly ? classes.readOnlyTableCell : ''
  )

  const handleInputChangeSelect = useCallback(
    (event: unknown, billingIndex: number) => {
      const { value } = (event as React.ChangeEvent<HTMLInputElement>).target
      if (productPlan) {
        const offerPlan = convertProductPlanToOfferPlan(productPlan)
        const billingTerm = offerPlan?.pricing?.recurrentPrice?.prices?.find(
          item =>
            `${item?.billingTerm?.value}${' '}${item?.billingTerm.type}` ===
            value
        )
        if (billingTerm) {
          const updatedPrices = cloneDeep(planRow.pricing.recurrentPrice.prices)
          updatedPrices[billingIndex] = { ...billingTerm }

          const updatedOriginalPrices = cloneDeep(
            planRow?.originalPricing?.recurrentPrice?.prices
          )
          if (updatedOriginalPrices) {
            updatedOriginalPrices[billingIndex] = { ...billingTerm }
          }
          const updatePlanRow = {
            ...planRow,
            pricing: {
              ...planRow.pricing,
              recurrentPrice: {
                ...planRow.pricing.recurrentPrice,
                prices: [...updatedPrices],
              },
            },
            originalPricing: {
              ...planRow.originalPricing,
              recurrentPrice: {
                ...(planRow.originalPricing?.recurrentPrice as RecurrentPrice),
                prices: [...(updatedOriginalPrices || [])],
              },
            },
          }

          updatePlanId &&
            updatePlanId(index, {
              ...updatePlanRow,
            } as unknown as AzureOfferPlan)
        }
      }
    },
    [planRow, productPlan]
  )

  const planBillings = productPlan
    ? convertProductPlanToOfferPlan(
        productPlan
      )?.pricing?.recurrentPrice?.prices?.map(item =>
        getBillingTermsList(item?.billingTerm, billingTermsData)
      )
    : []

  const addBillingTerms = () => {
    const updatedPrices = cloneDeep(recurrentPrices)

    const updatedRecurrentPrice = {
      ...planRow.pricing.recurrentPrice,
      prices: [...updatedPrices, emptyBillingTerms],
    }

    const updatePlanRow = {
      ...planRow,
      pricing: {
        ...planRow.pricing,
        recurrentPrice: {
          ...updatedRecurrentPrice,
        },
      },
      originalPricing: {
        ...planRow.originalPricing,
        recurrentPrice: {
          ...(planRow?.originalPricing?.recurrentPrice as RecurrentPrice),
          prices: [
            ...(planRow?.originalPricing?.recurrentPrice?.prices || []),
            emptyBillingTerms,
          ],
        },
      },
    }
    updatePlanId && updatePlanId(index, { ...updatePlanRow })
  }

  const isAddNewBillingTermsDisabled =
    billingTermsData?.length === (planBillings || [])?.length
  //TODO: https://ibexlabs.atlassian.net/browse/LT-5652
  return (
    <div>
      <div
        className={clsx(
          classes.subHeadingContainer,
          classes.companyDetailSubHead,
          classes.metering
        )}
      >
        <Typography
          className={clsx(
            classes.subHeading,
            isReadOnly ? classes.subHeadingReadOnly : ''
          )}
          data-testid="billing-terms-heading"
        >
          Billing terms
        </Typography>
      </div>
      <div>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead className={classes.tableHader}>
              <TableRow>
                {billingTermsColumn.map(col => (
                  <TableCell
                    className={clsx(
                      classes.headerTitle,
                      classes.tableCell,
                      isReadOnly ? classes.readOnlyTableCell : ''
                    )}
                    data-testid={'metering-column'}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {billingTermsData.map((billingTermItem, billingItemIndex) => {
                const billingErrorObj = planRowError
                  ? (get(planRowError, 'prices') as Record<string, unknown>[])[
                      billingItemIndex
                    ]
                  : {}

                const billingPriceValue = isNonEmptyNumber(
                  billingTermItem.pricePerPaymentInUsd
                )
                  ? billingTermItem.pricePerPaymentInUsd!
                  : ''
                return (
                  <TableRow data-testid={'billing-row'}>
                    <TableCell className={tableDataCellClass} width={'150px'}>
                      {!isReadOnly ? (
                        <FormControl className={classes.formWidth}>
                          <InputLabel
                            id="plan-billing-label"
                            className={classes.inputLabel}
                            classes={{ shrink: commonClasses.shrink }}
                          >
                            Select plan
                          </InputLabel>
                          <Select
                            labelId="product-plan-billing"
                            value={
                              `${billingTermItem?.billingTerm?.value}${' '}${
                                billingTermItem?.billingTerm?.type
                              }` || ''
                            }
                            onChange={e =>
                              handleInputChangeSelect(e, billingItemIndex)
                            }
                            variant="standard"
                            label="Select billing term"
                            inputProps={{ 'data-testid': 'input' }}
                            className={classes.selectField}
                            IconComponent={props => (
                              <KeyboardArrowDownIcon
                                {...props}
                                fontSize="small"
                                data-testid="arrow-button"
                              />
                            )}
                            classes={{ icon: classes.icon }}
                            data-testid="plan-billing-name"
                          >
                            {(planBillings || []).map(planBillingItem => (
                              <MenuItem
                                data-testid="menu-item"
                                value={planBillingItem?.value}
                                className={classes.menuRoot}
                                disabled={planBillingItem.disabled}
                                key={`${planBillingItem?.value}`}
                              >
                                {planBillingItem.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <div>
                          {billingTermItem?.billingTerm?.value}{' '}
                          {billingTermItem?.billingTerm?.type}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={tableDataCellClass} width={'150px'}>
                      {getPaymentOptionDisplayValue(
                        billingTermItem.billingTerm,
                        billingTermItem.paymentOption
                      )}
                    </TableCell>
                    <TableCell className={tableDataCellClass} width={'220px'}>
                      {shouldShowInput ? (
                        <div className={classes.billingPrice}>
                          <TextInput
                            type="number"
                            title={billingTermsColumn[2]}
                            placeHolder={billingTermsColumn[2]}
                            errorText={
                              billingErrorObj?.pricePerPaymentInUsd as string
                            }
                            isError={
                              !isEmpty(
                                billingErrorObj?.pricePerPaymentInUsd || ''
                              )
                            }
                            isReadOnly={false}
                            isRequired={true}
                            value={billingPriceValue}
                            updatedValue={billingPriceValue}
                            onChange={updatePrice(billingItemIndex)}
                            keyName="billingTermItem"
                            minNumber={0.001}
                            variant="regularV2"
                            keyToPrevent={['+', '-', 'e']}
                          />
                        </div>
                      ) : (
                        <DisplayValueData
                          isPrice={true}
                          id="billingTermItem"
                          value={
                            isViewPage
                              ? billingTermItem.pricePerPaymentInUsd!
                              : getDiscountedPriceValue({
                                  price: billingTermItem.pricePerPaymentInUsd!,
                                  ...(isDiscountPricingType
                                    ? {
                                        discount:
                                          planRow?.discountPercentage || 0,
                                      }
                                    : {}),
                                })
                          }
                        />
                      )}
                    </TableCell>
                    <TableCell className={tableDataCellClass} width={'150px'}>
                      {billingTermsData?.length > 1 && !isReadOnly && (
                        <IconButton
                          className={classes.closeIconButton}
                          onClick={() => deleteBillingTerms(billingItemIndex)}
                          data-testid="delete-billing-plan"
                          size="large"
                        >
                          <HighlightOffIcon className={classes.closeIcon} />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      {!isReadOnly && (
        <div className={classes.addBtncontainer}>
          <AddUpdateButton
            label={'Add billing term'}
            disabled={isAddNewBillingTermsDisabled}
            type={'add'}
            icon={AddCircleOutlineIcon}
            onClick={addBillingTerms}
          />
        </div>
      )}
    </div>
  )
}
